/**
 * Confidence-state badge helpers.
 *
 * Pure JS, no React / axios / UI deps so Jest can import these directly without
 * pulling in the whole RightsizingRecommendations chain. RightsizingRecommendations.js
 * imports + renders the resolved props; tests assert on the resolution itself.
 */

export const stabilityLabel = (n) => (n >= 0.95 ? 'High' : n >= 0.75 ? 'Medium' : 'Low');
export const maturityLabel = (n) => (n >= 0.95 ? 'Full' : n >= 0.5 ? 'Building' : 'New');

// Backend writes confidence_score on a 0–1 scale. Legacy ML/p95 paths sometimes
// ship 0–100. Treat ≤1 as fractional.
export function normalizeConfidencePercent(raw) {
  if (raw == null || isNaN(raw)) return 0;
  return Math.round(raw <= 1 ? raw * 100 : raw);
}

// Build the labeled tooltip for the components breakdown. Plain string for the
// native title="" attribute (the rest of this UI uses native titles).
//
// The base section (coverage/freshness/stability/maturity) renders for
// every rec. The SLI section ONLY appears when the backend included
// sli_* keys in components — driven by Phase 1.5 SLI-aware adjustment
// (sli_confidence_adjuster.py). When SLO is off or SLI data unavailable,
// the backend omits sli_* keys and the tooltip looks identical to today's.
export function buildComponentsTooltip(components, finalPct) {
  if (!components) return `${finalPct}% confidence`;
  const cov = Math.round((components.coverage ?? 0) * 100);
  const fre = Math.round((components.freshness ?? 0) * 100);
  const sta = stabilityLabel(components.stability ?? 0);
  const mat = maturityLabel(components.maturity ?? 0);
  const lines = [
    `Coverage:   ${cov}%`,
    `Freshness:  ${fre}%`,
    `Stability:  ${sta}`,
    `Maturity:   ${mat}`,
  ];
  // Append SLI breakdown when the backend resolved any SLI signal.
  // sli_total_adjustment is always present when any other sli_* key is.
  if (components.sli_total_adjustment !== undefined &&
      components.sli_total_adjustment !== null) {
    const fmtSigned = (v) => {
      if (v == null) return null;
      const r = Math.round(v * 10) / 10;
      return (r >= 0 ? '+' : '') + r;
    };
    lines.push('─── SLI signal ───');
    const headroom = fmtSigned(components.sli_headroom_contribution);
    const stab = fmtSigned(components.sli_stability_contribution);
    const corr = fmtSigned(components.sli_correlation_contribution);
    if (headroom !== null) lines.push(`Latency headroom: ${headroom}`);
    if (stab !== null)     lines.push(`SLI stability:    ${stab}`);
    if (corr !== null)     lines.push(`CPU↔latency:      ${corr}`);
    lines.push(`SLI net adjust:   ${fmtSigned(components.sli_total_adjustment)}`);
    if (components.sli_sample_count) {
      lines.push(`(${components.sli_sample_count} samples / ${components.sli_lookback_minutes}min)`);
    }
  }
  lines.push('─────────────────');
  lines.push(`Confidence: ${finalPct}%`);
  return lines.join('\n');
}

// Resolve the badge's visible state into props (color / label / sublabel /
// tooltip / hideRecommendation). Tests assert on the return value.
export function resolveBadgeProps(recommendation, confidencePercent) {
  const state = recommendation?.confidence_state || 'ok';
  const components = recommendation?.confidence_components;
  const ageMinutes = recommendation?.workload_age_minutes;

  if (state === 'learning') {
    const ageStr = typeof ageMinutes === 'number' ? `${Math.round(ageMinutes)}m of 60m` : '< 60m';
    return {
      state,
      color: 'bg-gray-200 text-gray-800',
      label: `Still learning — ${ageStr}`,
      tooltip: `Workload is too new to make a confident recommendation (${ageStr})`,
      hideRecommendation: true,
    };
  }
  if (state === 'initial') {
    return {
      state,
      color: 'bg-blue-100 text-blue-800',
      label: `Initial estimate · ${confidencePercent}%`,
      tooltip: buildComponentsTooltip(components, confidencePercent),
    };
  }
  if (state === 'recomputing') {
    return {
      state,
      color: 'bg-amber-100 text-amber-800',
      label: `Refreshing · ${confidencePercent}%`,
      sublabel: 'Using previous baseline while recalculating',
      tooltip: `Refreshing baseline · using previous baseline while recalculating\n${buildComponentsTooltip(components, confidencePercent)}`,
    };
  }
  if (state === 'limited') {
    return {
      state,
      color: 'bg-amber-100 text-amber-800',
      label: `Limited data · ${confidencePercent}%`,
      tooltip: `Limited data · agent not reporting recent metrics\n${buildComponentsTooltip(components, confidencePercent)}`,
    };
  }
  // ok: standard tier coloring
  let color = 'bg-gray-100 text-gray-800';
  if (confidencePercent >= 80) color = 'bg-green-100 text-green-800';
  else if (confidencePercent >= 60) color = 'bg-yellow-100 text-yellow-800';
  else color = 'bg-red-100 text-red-800';
  return {
    state,
    color,
    label: `${confidencePercent}% Confidence`,
    tooltip: buildComponentsTooltip(components, confidencePercent),
  };
}
